/**
 * pdp-video.js
 * ---------------------------------------------------------------------------
 * Enhances self-hosted product videos inside the PDP media grid (Prestige
 * "product-gallery" component) with a modern, accessible playback experience:
 *
 *   • Desktop (hover-capable, fine pointer): HOVER TO PLAY. Moving the cursor
 *     over a video plays it muted; moving to a different video stops the first
 *     and plays the new one. Only one video ever plays at a time.
 *   • Touch / no-hover devices (mobile carousel): unchanged — muted autoplay
 *     when a video scrolls into view, auto-pause when it scrolls out of view.
 *   • A clear, always-available "Tap for sound" control plus a persistent
 *     sound toggle so customers can turn audio on/off at any time.
 *   • Simple play / pause controls.
 *   • An expand / full-screen control.
 *   • Only one video is ever audible at a time (unmuting one mutes the others).
 *
 * Design notes:
 *   - Progressive enhancement. The theme renders native <video controls> as a
 *     fallback; this script only takes over once it successfully initialises.
 *     If the script fails to load, customers still get native controls.
 *   - Dynamic & reusable. Every gallery flagged with [data-pdp-video-controls]
 *     is enhanced, for any number of videos, on any product page (main product,
 *     featured product, quick-buy). New markup injected by variant re-renders,
 *     the quick-buy drawer, or the theme editor is picked up automatically.
 *   - It only targets Shopify-hosted videos (a real <video> element). Hosted
 *     external videos (YouTube / Vimeo iframes) keep the theme's default
 *     behaviour, as custom controls there require the provider iframe APIs.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Guard against the asset being included more than once on a page (e.g. a
  // page with several featured-product sections). Only the first run wins.
  if (window.__pdpVideoInitialized) return;
  window.__pdpVideoInitialized = true;

  var GALLERY_SELECTOR = 'product-gallery[data-pdp-video-controls]';
  var CELL_SELECTOR = '.product-gallery__media[data-media-type="video"]';
  var READY_ATTR = 'data-pdp-video-ready';

  // The controller that currently "owns" audio. Only one video plays sound at
  // a time so the page never becomes a wall of competing soundtracks.
  var soundOwner = null;

  // On desktop (a device with a hover-capable, fine pointer) videos play on
  // hover instead of on scroll-into-view. `hoverOwner` is the controller whose
  // video is currently playing because the cursor is over it; hovering a
  // different video stops this one first, so only one ever plays at a time.
  var HOVER_TO_PLAY =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var hoverOwner = null;

  // ---------------------------------------------------------------------------
  // Icons (inline so the asset stays self-contained). Stroke icons inherit
  // currentColor, matching the theme's icon language.
  // ---------------------------------------------------------------------------
  var ICONS = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 5.14v13.72a.5.5 0 0 0 .77.42l10.29-6.86a.5.5 0 0 0 0-.84L8.77 4.72A.5.5 0 0 0 8 5.14Z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M7 4.5h3.2v15H7zM13.8 4.5H17v15h-3.2z"/></svg>',
    soundOn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M11 5 6 9H2v6h4l5 4z" fill="currentColor" stroke="none"/><path d="M15.5 8.6a5 5 0 0 1 0 6.8"/><path d="M18.4 5.6a9 9 0 0 1 0 12.8"/></svg>',
    soundOff: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M11 5 6 9H2v6h4l5 4z" fill="currentColor" stroke="none"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>',
    expand: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>',
    compress: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 3v3a2 2 0 0 1-2 2H3M16 3v3a2 2 0 0 0 2 2h3M21 16h-3a2 2 0 0 0-2 2v3M3 16h3a2 2 0 0 1 2 2v3"/></svg>'
  };

  // Accessible labels. Overridable per-gallery via data-* attributes so the
  // strings stay translatable if the merchant needs another language.
  function stringsFor(gallery) {
    var d = gallery.dataset;
    return {
      play: d.pdpStrPlay || 'Play video',
      pause: d.pdpStrPause || 'Pause video',
      soundOn: d.pdpStrSoundOn || 'Turn sound on',
      soundOff: d.pdpStrSoundOff || 'Turn sound off',
      tapForSound: d.pdpStrTapForSound || 'Tap for sound',
      fullscreen: d.pdpStrFullscreen || 'View full screen',
      exitFullscreen: d.pdpStrExitFullscreen || 'Exit full screen'
    };
  }

  // ---------------------------------------------------------------------------
  // Fullscreen helpers (cross-browser, incl. iOS <video>-only fallback).
  // ---------------------------------------------------------------------------
  function fullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function exitFullscreen() {
    if (document.exitFullscreen) return document.exitFullscreen();
    if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  }

  // ---------------------------------------------------------------------------
  // A single shared IntersectionObserver drives autoplay/pause for every video
  // on the page. Threshold 0.5 => a video plays once it's at least half visible.
  // ---------------------------------------------------------------------------
  var observer = null;
  var controllers = new WeakMap();

  function getObserver() {
    if (observer) return observer;
    if (!('IntersectionObserver' in window)) return null;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var controller = controllers.get(entry.target);
        if (!controller) return;
        if (entry.isIntersecting) {
          controller.onEnterView();
        } else {
          controller.onLeaveView();
        }
      });
    }, { threshold: 0.5 });
    return observer;
  }

  // ---------------------------------------------------------------------------
  // Controller: one instance per video media cell.
  // ---------------------------------------------------------------------------
  function PdpVideo(cell, gallery) {
    this.cell = cell;
    this.gallery = gallery;
    this.media = cell.querySelector('video-media');
    this.video = cell.querySelector('video');
    this.strings = stringsFor(gallery);
    this.userPaused = false; // set true only when the customer explicitly pauses
    this.inView = false;
  }

  PdpVideo.prototype.init = function () {
    if (!this.video) return false; // external video (iframe) — leave theme default

    var video = this.video;

    // Start muted (required for autoplay) and inline; loop follows the theme's
    // "enable_video_looping" setting, which is already reflected on the element.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    video.controls = false;
    if (!video.getAttribute('preload') || video.getAttribute('preload') === 'none') {
      video.preload = 'metadata';
    }

    // Neutralise the theme's competing playback logic: drop "group" so grid
    // videos don't mute/pause each other. (The gallery-level "autoplay-media"
    // strategy is removed once, in scan(), and the poster stays visible until
    // the video actually plays — "loaded" is set in play().)
    if (this.media) {
      this.media.removeAttribute('group');
    }

    this.cell.classList.add('pdp-video-cell');
    this.buildControls();
    this.bindEvents();

    if (HOVER_TO_PLAY) {
      // Desktop: play only while the cursor is over the video. Leave it paused
      // on its poster until then — no scroll-into-view autoplay.
      this.bindHover();
    } else {
      // Touch / no-hover: keep viewport-driven autoplay.
      var io = getObserver();
      if (io) {
        controllers.set(this.cell, this);
        io.observe(this.cell);
      } else {
        // No IntersectionObserver support: just autoplay muted immediately.
        this.play();
      }
    }

    this.render();
    return true;
  };

  PdpVideo.prototype.buildControls = function () {
    var s = this.strings;

    var ui = document.createElement('div');
    ui.className = 'pdp-video__ui';

    // Full-surface tap target to toggle play/pause (mouse/touch convenience).
    // Non-focusable and hidden from assistive tech — keyboard and screen-reader
    // users operate the labelled buttons in the control bar instead.
    var hit = document.createElement('div');
    hit.className = 'pdp-video__hit';
    hit.setAttribute('aria-hidden', 'true');
    ui.appendChild(hit);

    // Prominent, easy-to-find "Tap for sound" affordance (shown while muted).
    var badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'pdp-video__sound-badge';
    badge.setAttribute('aria-label', s.soundOn);
    badge.innerHTML = ICONS.soundOff + '<span>' + s.tapForSound + '</span>';
    ui.appendChild(badge);

    // Control bar: play/pause on the left, sound + fullscreen on the right.
    var bar = document.createElement('div');
    bar.className = 'pdp-video__bar';

    var playBtn = this.makeButton('pdp-video__btn pdp-video__btn--play', s.pause, ICONS.pause);
    var spacer = document.createElement('span');
    spacer.className = 'pdp-video__spacer';
    var soundBtn = this.makeButton('pdp-video__btn pdp-video__btn--sound', s.soundOn, ICONS.soundOff);
    var fsBtn = this.makeButton('pdp-video__btn pdp-video__btn--fs', s.fullscreen, ICONS.expand);

    bar.appendChild(playBtn);
    bar.appendChild(spacer);
    bar.appendChild(soundBtn);
    bar.appendChild(fsBtn);
    ui.appendChild(bar);

    this.cell.appendChild(ui);

    this.ui = ui;
    this.hit = hit;
    this.badge = badge;
    this.playBtn = playBtn;
    this.soundBtn = soundBtn;
    this.fsBtn = fsBtn;
  };

  PdpVideo.prototype.makeButton = function (className, label, icon) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.setAttribute('aria-label', label);
    btn.innerHTML = icon;
    return btn;
  };

  PdpVideo.prototype.bindEvents = function () {
    var self = this;

    function stop(handler) {
      return function (event) {
        event.preventDefault();
        event.stopPropagation();
        handler.call(self, event);
      };
    }

    this.hit.addEventListener('click', stop(this.togglePlay));
    this.playBtn.addEventListener('click', stop(this.togglePlay));
    this.soundBtn.addEventListener('click', stop(this.toggleSound));
    this.badge.addEventListener('click', stop(this.unmute));
    this.fsBtn.addEventListener('click', stop(this.toggleFullscreen));

    // Keep the UI in sync with the underlying media element (the source of truth).
    this.video.addEventListener('play', function () { self.render(); });
    this.video.addEventListener('pause', function () { self.render(); });
    this.video.addEventListener('volumechange', function () { self.render(); });

    this._onFsChange = function () { self.render(); };
    document.addEventListener('fullscreenchange', this._onFsChange);
    document.addEventListener('webkitfullscreenchange', this._onFsChange);
  };

  // ----- Viewport-driven autoplay ------------------------------------------
  PdpVideo.prototype.onEnterView = function () {
    this.inView = true;
    if (!this.userPaused) this.play();
  };

  PdpVideo.prototype.onLeaveView = function () {
    this.inView = false;
    // Pause silently (not a user pause) so it resumes when scrolled back.
    if (!this.video.paused) this.video.pause();
  };

  // ----- Desktop hover-to-play ---------------------------------------------
  // `mouseenter`/`mouseleave` (not over/out) fire only when the cursor crosses
  // the cell's own boundary, so moving over the control buttons inside doesn't
  // spuriously stop playback.
  PdpVideo.prototype.bindHover = function () {
    var self = this;
    this._onMouseEnter = function () { self.hoverPlay(); };
    this._onMouseLeave = function () { self.hoverStop(); };
    this.cell.addEventListener('mouseenter', this._onMouseEnter);
    this.cell.addEventListener('mouseleave', this._onMouseLeave);
  };

  PdpVideo.prototype.hoverPlay = function () {
    // Stop whichever video is currently hover-playing, then take over.
    if (hoverOwner && hoverOwner !== this) hoverOwner.hoverStop();
    hoverOwner = this;
    this.userPaused = false;
    this.play();
  };

  PdpVideo.prototype.hoverStop = function () {
    if (hoverOwner === this) hoverOwner = null;
    if (!this.video.paused) this.video.pause();
    // Drop any sound and rewind so the next hover starts the clip fresh.
    this.mute();
    try { this.video.currentTime = 0; } catch (e) {}
  };

  // ----- Playback -----------------------------------------------------------
  PdpVideo.prototype.play = function () {
    if (this.media) this.media.setAttribute('loaded', '');
    var promise = this.video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () { /* autoplay may be blocked until interaction */ });
    }
  };

  PdpVideo.prototype.togglePlay = function () {
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  };

  // ----- Sound --------------------------------------------------------------
  PdpVideo.prototype.toggleSound = function () {
    if (this.video.muted) {
      this.unmute();
    } else {
      this.mute();
    }
  };

  PdpVideo.prototype.unmute = function () {
    // Enforce single-audio: mute whoever currently owns sound.
    if (soundOwner && soundOwner !== this) soundOwner.mute();
    soundOwner = this;
    this.video.muted = false;
    // Unmuting is an intent to watch — make sure it's actually playing.
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    }
    this.render();
  };

  PdpVideo.prototype.mute = function () {
    this.video.muted = true;
    if (soundOwner === this) soundOwner = null;
    this.render();
  };

  // ----- Fullscreen ---------------------------------------------------------
  PdpVideo.prototype.toggleFullscreen = function () {
    if (fullscreenElement()) {
      exitFullscreen();
      return;
    }
    var target = this.cell;
    if (target.requestFullscreen) {
      target.requestFullscreen().catch(function () {});
    } else if (target.webkitRequestFullscreen) {
      target.webkitRequestFullscreen();
    } else if (this.video.webkitEnterFullscreen) {
      // iPhone Safari: only the <video> element can go fullscreen (native UI).
      this.video.webkitEnterFullscreen();
    }
  };

  // ----- UI state -----------------------------------------------------------
  PdpVideo.prototype.render = function () {
    var s = this.strings;
    var paused = this.video.paused;
    var muted = this.video.muted;
    var isFs = fullscreenElement() === this.cell;

    this.playBtn.innerHTML = paused ? ICONS.play : ICONS.pause;
    this.playBtn.setAttribute('aria-label', paused ? s.play : s.pause);

    this.soundBtn.innerHTML = muted ? ICONS.soundOff : ICONS.soundOn;
    this.soundBtn.setAttribute('aria-label', muted ? s.soundOn : s.soundOff);

    this.fsBtn.innerHTML = isFs ? ICONS.compress : ICONS.expand;
    this.fsBtn.setAttribute('aria-label', isFs ? s.exitFullscreen : s.fullscreen);

    // The "Tap for sound" badge is only useful while the video plays muted.
    var showBadge = muted && !paused;
    this.cell.classList.toggle('pdp-video-cell--muted-playing', showBadge);
    this.cell.classList.toggle('pdp-video-cell--paused', paused);
    this.cell.classList.toggle('pdp-video-cell--fs', isFs);
  };

  // ---------------------------------------------------------------------------
  // Discovery / bootstrap
  // ---------------------------------------------------------------------------
  function enhanceCell(cell, gallery) {
    if (cell.getAttribute(READY_ATTR) === 'true') return;
    var controller = new PdpVideo(cell, gallery);
    if (controller.init()) {
      cell.setAttribute(READY_ATTR, 'true');
    }
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(GALLERY_SELECTOR).forEach(function (gallery) {
      gallery.querySelectorAll(CELL_SELECTOR).forEach(function (cell) {
        enhanceCell(cell, gallery);
      });
    });
    // Also handle the case where `root` itself is a gallery or a video cell
    // (e.g. a node added directly by a mutation).
    if (root && root.matches) {
      if (root.matches(GALLERY_SELECTOR)) {
        root.querySelectorAll(CELL_SELECTOR).forEach(function (cell) { enhanceCell(cell, root); });
      } else if (root.matches(CELL_SELECTOR)) {
        var g = root.closest(GALLERY_SELECTOR);
        if (g) enhanceCell(root, g);
      }
    }
  }

  // Watch for galleries injected after load: variant re-renders, quick-buy
  // drawer, Shopify theme editor section reloads, etc.
  var scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scanQueued = false;
      scan(document);
    });
  }

  function startMutationObserver() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          queueScan();
          return;
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    scan(document);
    startMutationObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
